import { type HcpDocumentStatusValue } from "@clipboard-health/contract-documents-service";
import { isDefined } from "@clipboard-health/util-ts";
import { useDebounce } from "@src/appV2/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { type Control, type FieldErrors, type FieldValues, useForm } from "react-hook-form";

import {
  type DefinitionOption,
  toDefinitionOption,
  type UseSearchEvidenceDefinitions,
  useSearchEvidenceDefinitions,
} from "../../api/useSearchEvidenceDefinitions";
import {
  type EvidenceData,
  type EvidenceDefinition,
  type JsonSchema,
  toJsonSchema,
} from "../../types";
import {
  cleanProperties,
  enhanceEvidenceProperties,
  type EsignTemplatePrefill,
  isUnknownEvidenceDefinition,
} from "../../utils";

const APPROVED_STATUS: HcpDocumentStatusValue = "APPROVED";
const SEARCH_DEBOUNCE_MS = 300;

export interface UseEvidenceFormArguments {
  evidence: EvidenceData | undefined;
  /**
   * The evidence's already-linked definition, hydrated by id from the evidences
   * fetch's `included` array (`include=evidenceDefinition`). `undefined` when the
   * evidence has no linked definition or the linked definition no longer exists.
   */
  linkedDefinition: EvidenceDefinition | undefined;
  documentStatus: HcpDocumentStatusValue | undefined;
  /**
   * The definition-search hook for this call site's auth context. Defaults to
   * the Cognito `apiClient` variant (admin Review Document modal). The Docs
   * Processor passes its `X-Authorization` wrapper so the Zendesk iframe (no
   * Cognito session) authenticates with its own principal instead of 401-ing.
   */
  useSearchDefinitions?: UseSearchEvidenceDefinitions;
  /**
   * E-sign template IDs from the v1 requirement being approved. When the
   * selected definition's schema declares `helloSignTemplateId` /
   * `pandaDocTemplateId`, these pre-fill the matching, still-empty fields so the
   * reviewer doesn't have to copy the facility's template hash by hand. Memoize
   * at the call site so the prefill effect doesn't re-run every render.
   */
  esignTemplatePrefill?: EsignTemplatePrefill;
}

export interface EvidenceFormSnapshot {
  evidenceId: string | undefined;
  evidenceDefinitionId: string;
  isUnknownDefinitionSelected: boolean;
  hasLinkedEvidence: boolean;
  isEvidenceApproved: boolean;
  isApprovalBlocked: boolean;
  isDefinitionSelectionPending: boolean;
}

export interface EvidenceFormFieldProps {
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  selectedDefinition: EvidenceDefinition | undefined;
  selectedOption: DefinitionOption | null;
  definitionOptions: DefinitionOption[];
  searchInputValue: string;
  isSearchingDefinitions: boolean;
  hasSearchInput: boolean;
  propertySchema: JsonSchema | undefined;
  hasProperties: boolean;
  onDefinitionChange: (option: DefinitionOption | null) => void;
  onSearchInputChange: (value: string) => void;
  isDefinitionMissing: boolean;
  isDefinitionSelectionPending: boolean;
}

export interface UseEvidenceFormReturn {
  viewProps: EvidenceFormFieldProps;
  snapshot: EvidenceFormSnapshot;
  validate: () => Promise<boolean>;
  getCleanedProperties: () => Record<string, unknown>;
}

export function useEvidenceForm(input: UseEvidenceFormArguments): UseEvidenceFormReturn {
  const {
    evidence,
    linkedDefinition,
    documentStatus,
    useSearchDefinitions = useSearchEvidenceDefinitions,
    esignTemplatePrefill,
  } = input;

  // Two state primitives, each matching the nature of its data:
  //   - `selectedDefinition`: the full chosen definition object. It originates
  //     either from the by-id hydration (the linked definition) or from a search
  //     hit; both carry `propertySchema`, so the property form needs no extra fetch.
  //   - `properties`: dynamic JSON-schema-driven payload. RHF<FieldValues>
  //     handles arbitrary field paths registered at runtime by DynamicSchemaForm.
  // The only remaining cast is on `getValues("properties")`, which is inherent
  // to the dynamic shape — TS can't statically know what the schema dictates.
  const [selectedDefinition, setSelectedDefinition] = useState<EvidenceDefinition | undefined>(
    undefined
  );
  // `searchInput` is the *controlled* Autocomplete input text. Controlling it
  // keeps MUI from snapping the box back to the selected option's label (which
  // is what otherwise swallowed the reviewer's first keystroke).
  const [searchInput, setSearchInput] = useState("");

  // While the input still equals the selected definition's label we're "at rest"
  // (showing the current selection), so don't search for that label — let the
  // empty-state prefetch run instead. Any other input text is a live query.
  const selectedLabel = selectedDefinition?.attributes.displayName ?? "";
  const isShowingSelectedLabel = isDefined(selectedDefinition) && searchInput === selectedLabel;
  const searchQuery = isShowingSelectedLabel ? "" : searchInput;
  const debouncedSearch = useDebounce({ value: searchQuery, debounceTimeInMs: SEARCH_DEBOUNCE_MS });
  const { data: searchResults, isFetching: isSearchingDefinitions } = useSearchDefinitions({
    search: debouncedSearch,
    // Seed the picker with a default page of definitions before the reviewer types.
    prefetchOnEmpty: true,
  });

  const propertiesForm = useForm<FieldValues>({
    mode: "onSubmit",
    defaultValues: { properties: {} },
  });

  const evidenceId = evidence?.id;
  const evidenceLinkedDefinitionId = evidence?.relationships.evidenceDefinition.data.id;

  useEffect(() => {
    if (!evidence) {
      return;
    }
    // Seed the selection from the linked definition hydrated alongside the
    // evidence, and seed the input with its label so the box shows the current
    // mapping. `propertiesForm.reset` is stable across renders; re-run only when
    // the evidence identity (or its linked definition) changes.
    setSelectedDefinition(linkedDefinition);
    setSearchInput(linkedDefinition?.attributes.displayName ?? "");
    propertiesForm.reset({ properties: evidence.attributes.properties });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceId, evidenceLinkedDefinitionId, linkedDefinition]);

  const evidenceDefinitionId = selectedDefinition?.id ?? "";
  const propertySchema = selectedDefinition
    ? toJsonSchema(selectedDefinition.attributes.propertySchema)
    : undefined;
  const hasProperties = propertySchema
    ? Object.keys(propertySchema.properties ?? {}).length > 0
    : false;

  // Pre-fill the e-sign template IDs from the requirement being approved once a
  // definition that declares those fields is selected. Targeted `setValue` (not
  // `reset`) so it touches only the still-empty template fields and never
  // disturbs the reviewer's other entries. Re-runs when the selected definition
  // changes (new `evidenceDefinitionId`) or when the requirement's templates
  // resolve (new `esignTemplatePrefill`). `enhanceEvidenceProperties` already
  // gates on schema-declared + non-empty, so this is a no-op for generic
  // declinations and for fields the reviewer has filled.
  useEffect(() => {
    const prefillValues = enhanceEvidenceProperties(
      (propertiesForm.getValues("properties") as Record<string, unknown>) ?? {},
      propertySchema,
      esignTemplatePrefill
    );
    for (const [key, value] of Object.entries(prefillValues)) {
      propertiesForm.setValue(`properties.${key}`, value, { shouldDirty: false });
    }
    // `propertySchema` is intentionally omitted: it derives from the selected
    // definition, so `evidenceDefinitionId` already covers definition changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceDefinitionId, esignTemplatePrefill]);

  const isSearching = searchQuery.length > 0;
  const definitionOptions = useMemo<DefinitionOption[]>(() => {
    const searchOptions = searchResults.map(toDefinitionOption);
    // While the reviewer is typing a query, show only the search results.
    // At rest, surface the current selection so MUI can match the rendered value
    // even when it isn't a search hit (e.g. an auto-mapped UNKNOWN definition).
    if (isSearching || !isDefined(selectedDefinition)) {
      return searchOptions;
    }
    return searchOptions.some((option) => option.id === selectedDefinition.id)
      ? searchOptions
      : [toDefinitionOption(selectedDefinition), ...searchOptions];
  }, [isSearching, selectedDefinition, searchResults]);
  // Drop the Autocomplete value while searching so MUI neither flags a
  // value-not-in-options mismatch nor highlights the abandoned selection; the
  // selection state itself persists (for approval gating) until a new pick.
  const selectedOption =
    !isSearching && isDefined(selectedDefinition) ? toDefinitionOption(selectedDefinition) : null;

  // The picker box shows text that diverges from the committed selection's label
  // — the reviewer is mid-search, or backspaced the field, without committing a
  // pick. Submitting in this state would silently save the previously committed
  // definition rather than what's on screen, so the modal must block submit until
  // the reviewer commits a pick (or restores the selection's label).
  const isDefinitionSelectionPending = searchInput !== selectedLabel;

  const isUnknownDefinitionSelected = isUnknownEvidenceDefinition(selectedDefinition);
  const hasLinkedEvidence = evidence !== undefined;
  // The linked definition is missing when the evidence references one by id but
  // the by-id hydration produced nothing (linked definition deleted / 404'd).
  const isDefinitionMissing =
    hasLinkedEvidence && isDefined(evidenceLinkedDefinitionId) && !isDefined(linkedDefinition);
  const isEvidenceApproved =
    hasLinkedEvidence && evidenceDefinitionId !== "" && documentStatus === APPROVED_STATUS;
  const isApprovalBlocked =
    hasLinkedEvidence &&
    documentStatus === APPROVED_STATUS &&
    (evidenceDefinitionId === "" || isUnknownDefinitionSelected || isDefinitionMissing);

  function onDefinitionChange(option: DefinitionOption | null) {
    if (!option) {
      setSelectedDefinition(undefined);
      setSearchInput("");
      propertiesForm.reset({ properties: {} });
      return;
    }
    const chosen = searchResults.find((definition) => definition.id === option.id);
    if (!chosen) {
      return;
    }
    // Commit the pick: show its label in the box (which ends the search) and
    // reset properties only when the definition actually changed.
    setSearchInput(chosen.attributes.displayName);
    if (chosen.id !== selectedDefinition?.id) {
      setSelectedDefinition(chosen);
      propertiesForm.reset({ properties: {} });
    }
  }

  // Controlled input: just track the text. Typing that diverges from the
  // selected label becomes the live search query (see `searchQuery` above); the
  // selection state is left intact until the reviewer commits a new pick.
  function onSearchInputChange(value: string) {
    setSearchInput(value);
  }

  async function validate(): Promise<boolean> {
    if (!isEvidenceApproved) {
      return true;
    }
    return propertiesForm.trigger();
  }

  function getCleanedProperties(): Record<string, unknown> {
    const properties = propertiesForm.getValues("properties") as Record<string, unknown>;
    if (!propertySchema) {
      return properties;
    }
    return cleanProperties(properties, propertySchema);
  }

  return {
    viewProps: {
      control: propertiesForm.control,
      errors: propertiesForm.formState.errors,
      selectedDefinition,
      selectedOption,
      definitionOptions,
      searchInputValue: searchInput,
      isSearchingDefinitions,
      hasSearchInput: debouncedSearch.length > 0,
      propertySchema,
      hasProperties,
      onDefinitionChange,
      onSearchInputChange,
      isDefinitionMissing,
      isDefinitionSelectionPending,
    },
    snapshot: {
      evidenceId,
      evidenceDefinitionId,
      isUnknownDefinitionSelected,
      hasLinkedEvidence,
      isEvidenceApproved,
      isApprovalBlocked,
      isDefinitionSelectionPending,
    },
    validate,
    getCleanedProperties,
  };
}
